import { useAuth } from '@/contexts/AuthContext';
import { useBatch } from '@/contexts/BatchContext';
import { User } from '@/types';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Menu, Bell, ChevronDown, LogOut, Building2 } from 'lucide-react';
import BatchSelector from './BatchSelector';
import BayesPrepLogo from './BayesPrepLogo';

interface HeaderProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  user: User | null;
}

const Header = ({ sidebarOpen, setSidebarOpen, user }: HeaderProps) => {
  const { logout } = useAuth();
  // We need the context but don't use currentBatch directly
  useBatch();

  // Get tenant name from localStorage
  const tenantCode = localStorage.getItem('tenantCode') || 'Unknown';

  return (
    <header className="sticky top-0 bg-white z-30 border-b border-gray-200">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Header: Left side */}
          <div className="flex items-center space-x-4">
            {/* Hamburger button */}
            <div className="hamburger-menu">
              <button
                className="text-gray-500 hover:text-gray-600 lg:hidden z-50 p-2 -ml-2"
                aria-controls="sidebar"
                aria-expanded={sidebarOpen}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent event bubbling
                  setSidebarOpen(!sidebarOpen);
                  console.log('Hamburger clicked, setting sidebar to:', !sidebarOpen);
                }}
              >
                <span className="sr-only">Open sidebar</span>
                <Menu className="w-6 h-6" />
              </button>
            </div>

            {/* BayesPrep Logo */}
            <div className="hidden lg:flex">
              <BayesPrepLogo size="md" showText={true} variant="admin" />
            </div>
          </div>

          {/* Header: Right side */}
          <div className="flex items-center space-x-4">
            {/* Tenant Name */}
            <div className="hidden md:flex items-center space-x-2 px-3 py-1.5 bg-gray-50 rounded-md">
              <Building2 className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">{tenantCode}</span>
            </div>

            {/* Batch Selector */}
            <div className="hidden md:flex">
              <BatchSelector />
            </div>

            {/* Notifications */}
            <button className="p-1.5 rounded-full text-gray-500 hover:bg-gray-100">
              <Bell className="w-5 h-5" />
            </button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2 py-1.5 h-auto">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src="/avatar.svg" alt={user?.name || 'User'} />
                    <AvatarFallback>{user?.name?.charAt(0) || 'A'}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium hidden md:inline-block">
                    {user?.name ? user.name.split(' ').pop() : 'Admin'}
                  </span>
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
